import React from "react";
import { ArrowRight, CalendarDays, Check, Clock, ExternalLink, Lock, LockOpen, RotateCw } from "lucide-react";
import { differenceInDays } from "date-fns";
import { useTranslation } from "react-i18next";
import useSurveyActions from "./useSurveyActions";

export type SurveyStatus = "not-started" | "in-progress" | "completed";

// Same thresholds SurveyCard uses for its badge.
export const getSurveyStatus = (survey: any): SurveyStatus => {
  const progress = parseInt(survey?.progress) || 0;
  if (progress >= 100 || survey?.completed_at) return "completed";
  if (progress === 0) return "not-started";
  return "in-progress";
};

// A facilitator can lock a pipeline assessment; the list sends is_locked on
// each item. Missing (older backend) means open.
export const isSurveyLocked = (survey: any) => survey?.is_locked === true;

export const getDaysLeft = (survey: any) =>
  survey?.end_date ? differenceInDays(new Date(survey.end_date), new Date()) : null;

// No per-survey duration comes back from the API, so estimate ~30s a question.
export const getEstimatedMinutes = (survey: any) => {
  const questions = Number(survey?.number_of_questions) || 0;
  return questions ? Math.max(1, Math.ceil(questions / 2)) : null;
};

// A light tint of the brand colour for pill and track backgrounds.
export const tint = (colour: string, percent = 12) =>
  `color-mix(in srgb, ${colour} ${percent}%, white)`;

export const ProgressBar = ({ value, accent, className = "" }: { value: number, accent: string, className?: string }) => (
  <div className={`h-2 bg-gray-200 rounded-full overflow-hidden ${className}`}>
    <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, value))}%`, backgroundColor: accent }} />
  </div>
);

type SurveyRowProps = {
  survey: any,
  refetch: any,
  accent: string,
  useNeutralStatus: boolean,
  typeLabel: string,
};

const SurveyRow = ({ survey, refetch, accent, useNeutralStatus, typeLabel }: SurveyRowProps) => {
  const { t } = useTranslation();
  const { takeSurvey, viewResponse, isLoadingQuestions, overlays } = useSurveyActions({ survey, refetch });
  const status = getSurveyStatus(survey);
  const locked = isSurveyLocked(survey);
  const progress = Math.round(parseFloat(survey?.progress) || 0);
  const daysLeft = getDaysLeft(survey);
  const minutes = getEstimatedMinutes(survey);
  const questions = Number(survey?.number_of_questions) || 0;

  const dueText = status === "completed"
    ? (survey?.completed_at ? t("completed-on", { date: new Date(survey.completed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) }) : t("completed"))
    : daysLeft === null ? null
      : daysLeft > 0 ? t("due-in-days", { count: daysLeft })
        : daysLeft === 0 ? t("ends-today")
          : t("expired");

  const statusPill = {
    "not-started": (
      <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
        {t("not-started")}
      </span>
    ),
    "in-progress": (
      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap ${useNeutralStatus ? "bg-gray-100 text-gray-700" : "bg-blue-50 text-blue-600"}`}>
        <RotateCw className="w-3.5 h-3.5" />
        {t("in-progress")}
      </span>
    ),
    "completed": (
      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap ${useNeutralStatus ? "bg-gray-200 text-gray-900" : "bg-green-50 text-green-700"}`}>
        <Check className="w-3.5 h-3.5" />
        {t("completed")}
      </span>
    ),
  }[status];

  const LockIcon = locked || status === "completed" ? Lock : LockOpen;

  return (
    <div className="flex flex-col gap-3 px-4 py-4 md:grid md:grid-cols-[minmax(0,2.4fr)_minmax(0,1fr)_minmax(0,0.6fr)_minmax(0,1.4fr)_auto_auto] md:items-center md:gap-4 md:px-5">
      {overlays}
      <div className="flex items-start gap-4 min-w-0">
        <LockIcon
          className="w-7 h-7 shrink-0 mt-0.5"
          strokeWidth={2.25}
          style={{ color: locked || status === "completed" ? "#111827" : accent }}
          aria-label={locked ? t("closed") : t("open")}
        />
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 break-words">{survey?.group_title || survey?.survey_title}</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {typeLabel}
            {questions ? <><span className="mx-2">•</span>{t("questions-count", { count: questions })}</> : null}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-600 pl-11 md:contents">
        <span className="flex items-center gap-2 whitespace-nowrap">
          {dueText && <><CalendarDays className="w-4 h-4 text-gray-500" />{dueText}</>}
        </span>
        <span className="flex items-center gap-2 whitespace-nowrap">
          {minutes && <><Clock className="w-4 h-4 text-gray-500" />{t("minutes-short", { count: minutes })}</>}
        </span>
      </div>

      <div className="pl-11 md:pl-0">
        {status === "in-progress" && (
          <div className="flex items-center gap-3">
            <ProgressBar value={progress} accent={accent} className="flex-1 min-w-[80px]" />
            <span className="text-sm text-gray-600 whitespace-nowrap">{t("percent-complete", { percent: progress })}</span>
          </div>
        )}
      </div>

      <div className="pl-11 md:pl-0 flex md:justify-end">{statusPill}</div>

      <div className="pl-11 md:pl-0 md:w-[170px]">
        {status === "completed" ? (
          <button
            onClick={() => viewResponse(survey)}
            className="w-full flex items-center justify-center gap-2 border border-gray-300 bg-white text-gray-900 text-sm font-medium py-2.5 px-4 rounded-md hover:bg-gray-50"
          >
            {t("view-responses")}
            <ExternalLink className="w-4 h-4" />
          </button>
        ) : (
          <button
            id="take-survey"
            onClick={() => !locked && takeSurvey(survey)}
            disabled={locked || isLoadingQuestions}
            title={locked ? t("survey-locked") : undefined}
            style={{ backgroundColor: locked ? undefined : accent }}
            className={`w-full flex items-center justify-center gap-2 text-sm font-medium py-2.5 px-4 rounded-md ${locked ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "text-white hover:opacity-90"}`}
          >
            {locked ? <><Lock className="w-4 h-4" />{t("locked")}</> : (
              <>
                {status === "in-progress" ? t("continue") : t("take-survey")}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        )}
        {locked && <p className="text-xs text-gray-500 mt-1.5 text-center">{t("survey-locked")}</p>}
      </div>
    </div>
  );
};

export default SurveyRow;
